<script setup lang="ts">
import { computed, inject, onBeforeMount, ref, Ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { ECONOMY, SUFFIX_CHARTER, SUFFIX_GROUPB, SUFFIX_PUBLIC, SUFFIX_VACV, TYPE_BEST_PRICE, TYPE_PRICE_BOOKED } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { useListFlightStore } from '@/store/ListFlightStore';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import RadioButton from 'primevue/radiobutton';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import FaresAvailable from '@/components/modal/faresAvailable/FaresAvailable.vue';

const dialogRef = inject('dialogRef') as Ref;
const airInfo = dialogRef?.value.data.air;
const airIndex = dialogRef?.value.data.airIndex;
const passenger = dialogRef?.value.data.passenger;
const airPricing = dialogRef?.value.data.pricing;
const pricingIndex = dialogRef?.value.data.pricingIndex;
const quoteId = dialogRef?.value.data.quote.sk;
const permission = dialogRef?.value.data.role;
const populateWorksheet = dialogRef?.value.options.emits.populateWorksheet;
const detailClassesEmit = dialogRef?.value.options.emits.detailClassesEmit;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const currentLanguage = getCurrentLocale();
const airManagementStore = useAirManagementStore();
const listFlightStore = useListFlightStore();
const numberOfPassengers = ref(1);
const numberOfPassengersFares = ref<number[]>([]);
const selectMargins = ref<string[]>([]);
const selectMarginPrices = ref<number[]>([]);
const typePrice = ref(TYPE_BEST_PRICE);
const isHasPrice = ref(false);
const isLoading = ref(false);
const isHasCharterData = ref(false);
const classMarrginVariable = {
  published: 'published',
  group: 'groupsB',
  vac: 'vac',
  charter: 'charter'
};

const isShowCharter = ref(false);
const fareAvailableData = ref(null);
const classSelected = ref([]);
const hideShow = ref({
  classAvailable: true,
  fareAvailable: true
});
const detailClasses: any[] = [];

const expandPricingFunc = (type) => {
  hideShow.value[type] = !hideShow.value[type];
};

const handleUpdateQuote = () => {
  if (detailClassesEmit) {
    detailClassesEmit({ detailClasses, airIndex });
  }
  const marginFare = 0;
  const taxes = 0;
  selectMargins.value.forEach((item, index) => {
    const classFind = classSelected.value.find((select) => select.index === index);
    if (classFind) {
      const classIndexOf = classSelected.value.indexOf(classFind);
      classSelected.value[classIndexOf].data.fareTypeSelected = item;

      switch (item) {
        case classMarrginVariable.published:
          classSelected.value[classIndexOf].data.published = Number(classSelected.value[classIndexOf].data.published);
          classSelected.value[classIndexOf].data.taxes = Number(classSelected.value[classIndexOf].data.published) - Number(classSelected.value[classIndexOf].data.basePublishedPrice);
          classSelected.value[classIndexOf].data.marginFare = Number(classSelected.value[classIndexOf].data.basePublishedPrice) + Number(selectMarginPrices.value[index]);
          classSelected.value[classIndexOf].data.basePrice = Number(classSelected.value[classIndexOf].data.basePublishedPrice);
          classSelected.value[classIndexOf].data.suffixKey = SUFFIX_PUBLIC;
          break;
        case classMarrginVariable.group:
          classSelected.value[classIndexOf].data.groupsB = Number(classSelected.value[classIndexOf].data.groupsB);
          classSelected.value[classIndexOf].data.taxes = Number(classSelected.value[classIndexOf].data.groupsB) - Number(classSelected.value[classIndexOf].data.baseGroupsBPrice);
          classSelected.value[classIndexOf].data.marginFare = Number(classSelected.value[classIndexOf].data.baseGroupsBPrice) + Number(selectMarginPrices.value[index]);
          classSelected.value[classIndexOf].data.basePrice = Number(classSelected.value[classIndexOf].data.baseGroupsBPrice);
          classSelected.value[classIndexOf].data.suffixKey = SUFFIX_GROUPB;
          break;
        case classMarrginVariable.vac:
          classSelected.value[classIndexOf].data.vac = Number(classSelected.value[classIndexOf].data.vac);
          classSelected.value[classIndexOf].data.taxes = Number(classSelected.value[classIndexOf].data.vac) - Number(classSelected.value[classIndexOf].data.baseVacPrice);
          classSelected.value[classIndexOf].data.marginFare = Number(classSelected.value[classIndexOf].data.baseVacPrice) + Number(selectMarginPrices.value[index]);
          classSelected.value[classIndexOf].data.basePrice = Number(classSelected.value[classIndexOf].data.baseVacPrice);
          classSelected.value[classIndexOf].data.suffixKey = SUFFIX_VACV;
          break;
        case classMarrginVariable.charter:
          classSelected.value[classIndexOf].data.charter = Number(classSelected.value[classIndexOf].data.charter);
          classSelected.value[classIndexOf].data.taxes = Number(classSelected.value[classIndexOf].data.charter);
          classSelected.value[classIndexOf].data.marginFare = Number(classSelected.value[classIndexOf].data.charter) + Number(selectMarginPrices.value[index]);
          classSelected.value[classIndexOf].data.basePrice = Number(classSelected.value[classIndexOf].data.charter);
          classSelected.value[classIndexOf].data.paxFareSumAdult = Number(classSelected.value[classIndexOf].data.charter);
          classSelected.value[classIndexOf].data.paxFareSumChild = Number(classSelected.value[classIndexOf].data.charter);
          classSelected.value[classIndexOf].data.suffixKey = SUFFIX_CHARTER;
          break;
        default:
          break;
      }
    }
  });

  classSelected.value = classSelected.value.map((item) => {
    const classSelect = {
      ...item
    };
    classSelect.data.numberOfPassengersFares = numberOfPassengersFares.value[item.index] ?? 0;
    return classSelect;
  });

  populateWorksheet({
    marginFare,
    aTaxes: taxes.toFixed(2),
    classMargin: classesMargin.value,
    classSelected: classSelected.value,
    airIndex: airIndex,
    pricingIndex: pricingIndex
  });
};

const getDetailData = async () => {
  if (!passenger) {
    toast.add({ severity: 'error', summary: '', detail: t('worksheet.passengers_no_data') });
    return;
  }
  const segmentGroups = [];
  isHasPrice.value = false;
  airInfo?.flights.forEach((flight) => {
    segmentGroups.push({
      depDate: formatDate(flight.depDate, undefined, false),
      depCode: flight.gateCode,
      destCode: flight.destCode,
      marketingCompany: flight.operated,
      flightNumber: flight.flightNumber,
      airClass: flight.seatSelected
    });
  });
  isLoading.value = true;
  airManagementStore
    .findFareRequest({
      segmentGroups,
      numberOfPassengers: numberOfPassengers.value,
      typePrice: typePrice.value
    })
    .then(async (response) => {
      fareAvailableData.value = response.fareFamilies.map((el) => ({ ...el, type: airPricing.type, charter: 0 }));
      detailClasses[airIndex] = response.details;
      isHasPrice.value = true;

      // Use nextTick to ensure the DOM updates before scrolling
      await nextTick();
      // to prevent unit tests failing with undefined document
      if (document) {
        document.getElementById('price_show')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
      selectMarginPrices.value = Array(fareAvailableData.value?.length).fill(0);
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const classesMargin = computed(() => {
  const priceMargin = [];
  if (permission?.vacv_pricing_view) {
    priceMargin.push({
      value: 'VAC',
      key: classMarrginVariable.vac
    });
  }
  if (permission?.groupB_pricing_view) {
    priceMargin.push({
      value: t('worksheet.groupsb'),
      key: classMarrginVariable.group
    });
  }
  if (permission?.published_rate_view) {
    priceMargin.push({
      value: t('worksheet.published'),
      key: classMarrginVariable.published
    });
  }
  if (permission?.charter_pricing_view && isShowCharter.value) {
    priceMargin.push({
      value: t('worksheet.charter'),
      key: classMarrginVariable.charter
    });
  }
  return priceMargin;
});

const handleCharter = async () => {
  if (permission?.charter_pricing_view) {
    const payload = {
      classType: airPricing.type.toUpperCase(),
      gateDep: 'YYZ',
      gateRet: 'CUN',
      destDep: 'YYZ',
      destRet: 'CUN',
      depDate: '2023-11-04',
      retDate: '2023-11-11',
      language: 'en',
      nbAdult: '',
      flex: '',
      flexHigh: '',
      flexLow: '',
      maxNextAvail: '',
      maxResult: '',
      noneAdult1: '',
      noneAdult2: '',
      noneAdult3: '',
      noneAdult4: '',
      searchType: '',
      tourToDisplay: '',
      tourOp: '',
      depFlightName: 'AC930',
      retFlightName: 'AC1813'
    };
    const charterData = await listFlightStore.getCharter(payload, quoteId);
    fareAvailableData.value.forEach((el) => (el.charter = charterData.totpax));
    isHasCharterData.value = true;
  }
};

const renderListPassenger = (num) => {
  return Array.from({ length: num }, (_, index) => ({
    value: index + 1
  }));
};

const renderClass = (listClass) => {
  return Object.entries(listClass).map(([className, value]) => {
    return {
      className,
      value
    };
  });
};

const onClassSelected = (event: []) => {
  classSelected.value = event;
};

const onNumberOfPassengersFares = (event: []) => {
  numberOfPassengersFares.value = event;
};

const onSelectMarginPrices = (event: []) => {
  selectMarginPrices.value = event;
};

const onSelectMargins = (event: []) => {
  selectMargins.value = event;
};

const initData = () => {
  airInfo.flights.forEach((flight) => {
    flight.classFlightData = renderClass(flight.classesAvailable);
    if (airPricing.type.toUpperCase() === ECONOMY.toUpperCase()) {
      flight.seatSelected = renderClass(flight.classesAvailable).find((item) => item.className === 'Y')?.className ?? renderClass(flight.classesAvailable)[0]?.className;
    } else {
      flight.seatSelected = renderClass(flight.classesAvailable).find((item) => item.className === 'J')?.className ?? renderClass(flight.classesAvailable)[0]?.className;
    }
  });
};

onBeforeMount(() => {
  initData();
});
</script>

<template>
  <div>
    <LoadingSpinner v-if="isLoading" />
    <div class="flight-details">
      <div class="detail px-4">
        <DataTable :value="airInfo?.flights" class="w-full" showGridlines>
          <TableColumn field="flightNo" :header="$t('worksheet.flight_number')" dataType="string" class="min-w-10rem"></TableColumn>
          <TableColumn field="gate" :header="$t('worksheet.departure_city')" dataType="string" class="min-w-10rem"></TableColumn>
          <TableColumn field="dest" :header="$t('worksheet.arrival_city')" dataType="string" class="min-w-10rem"></TableColumn>
          <TableColumn field="depDate" :header="$t('worksheet.departure_date')" class="min-w-10rem">
            <template #body="{ data }">
              {{ formatDate(data?.depDate, 'dd-LLL-yyyy', false, currentLanguage) }}
            </template>
          </TableColumn>
          <TableColumn field="depTime" :header="$t('worksheet.departure_time')" dataType="date" class="min-w-10rem">
            <template #body="{ data }">
              {{ data?.depTime }}
            </template>
          </TableColumn>
          <TableColumn field="arrDate" :header="$t('worksheet.arrival_date')" dataType="date" class="min-w-10rem">
            <template #body="{ data }">
              {{ formatDate(data?.arrDate, 'dd-LLL-yyyy', false, currentLanguage) }}
            </template>
          </TableColumn>
          <TableColumn field="arrTime" :header="$t('worksheet.arrival_time')" dataType="date" class="min-w-10rem">
            <template #body="{ data }">
              {{ data?.arrTime }}
            </template>
          </TableColumn>
        </DataTable>
      </div>

      <div class="classes-available mt-4">
        <div class="mb-1">
          <div class="classes-available-header flex justify-content-between cursor-pointer" @click="expandPricingFunc('classAvailable')">
            <div class="flex align-items-center gap-10">
              <div>{{ $t('worksheet.class_available') }}</div>
              <div class="flex align-items-center ml-2">[&nbsp; <i class="pi pi-minus" v-if="hideShow?.classAvailable"></i> <i class="pi pi-plus" v-else></i>&nbsp;]</div>
            </div>
          </div>
          <div v-show="hideShow?.classAvailable">
            <div class="classes-available-detail">
              <div class="outbound-detail flex w-full" v-for="(flight, flightIndex) in airInfo?.flights" :key="'flight' + flightIndex">
                <div class="title w-2 p-3">{{ $t(`worksheet.${flight.type.toLowerCase()}`) }}</div>
                <div class="title w-2 p-3">
                  <div>{{ flight.flightNo }}</div>
                  <div class="flex gap-3 mt-3 flex-wrap align-items-center">
                    <div class="font-bold">{{ $t('common.class') }}</div>
                    <Select v-model="flight.seatSelected" :options="flight.classFlightData" optionLabel="className" optionValue="className" class="w-5rem" />
                  </div>
                </div>
                <div class="flex flex-wrap pb-5 gap-1 w-10">
                  <div class="p-2 flex flex-column" v-for="(value, property) in flight.classesAvailable" :key="'outbound' + property">
                    <div class="box-bodder">{{ property }}</div>
                    <div class="box-bodder">{{ value }}</div>
                  </div>
                </div>
              </div>
              <div class="p-2 flex justify-content-end gap-5 flex-wrap">
                <div class="flex gap-4">
                  <div class="flex gap-2 align-items-center">
                    <div>{{ $t('worksheet.number_of_passengers') }}</div>
                    <div>
                      <Select v-model="numberOfPassengers" :options="renderListPassenger(passenger)" optionLabel="value" optionValue="value" class="w-5rem" />
                    </div>
                  </div>
                  <div class="flex gap-2 align-items-center">
                    <div>{{ $t('worksheet.best_pricer') }}</div>
                    <div>
                      <RadioButton v-model="typePrice" name="typePrice" :value="TYPE_BEST_PRICE" />
                    </div>
                  </div>
                  <div class="flex gap-2 align-items-center">
                    <div>{{ $t('worksheet.price_as_booked') }}</div>
                    <div>
                      <RadioButton v-model="typePrice" name="typePrice" :value="TYPE_PRICE_BOOKED" />
                    </div>
                  </div>
                </div>
                <Button :label="$t('common.buttons.process')" @click="getDetailData()" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="fares-available mt-4" v-if="isHasPrice" id="price_show">
        <div class="mb-1">
          <div class="fares-available-header bg-blue-400 flex justify-content-between cursor-pointer" @click="expandPricingFunc('fareAvailable')">
            <div class="flex align-items-center gap-10">
              <div>{{ $t('worksheet.fare_available') }}</div>
              <div class="flex align-items-center ml-2">[&nbsp; <i class="pi pi-minus" v-if="hideShow?.fareAvailable"></i> <i class="pi pi-plus" v-else></i>&nbsp;]</div>
            </div>
          </div>
          <div v-show="hideShow?.fareAvailable">
            <div v-if="fareAvailableData" class="fares-available-detail">
              <FaresAvailable
                :fareAvailableData="fareAvailableData"
                :classesMargin="classesMargin"
                :listPassenger="renderListPassenger(passenger)"
                :isShowCharter="isShowCharter"
                @classSelected="onClassSelected($event)"
                @handleCharter="handleCharter()"
                @numberOfPassengersFares="onNumberOfPassengersFares($event)"
                @selectMarginPrices="onSelectMarginPrices($event)"
                @selectMargins="onSelectMargins($event)"
              ></FaresAvailable>
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-content-end mt-2" v-if="isHasPrice">
        <Button :disabled="!classSelected?.length" :label="$t('worksheet.populate_worksheet')" icon="pi pi-check" @click="handleUpdateQuote" />
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.min-w-10rem {
  min-width: 10rem;
}

.width-30 {
  width: 30%;
}

.width-15 {
  width: 15%;
}

.width-80 {
  width: 80%;
}

.classes-available-header,
.fares-available-header {
  color: $col-white;
  padding: 1.05rem 1.75rem;
  font-weight: 700;
}

.classes-available-detail {
  border: $border-greybd;
}

.outbound-detail {
  border-bottom: $border-greybd;
  .title {
    border-right: $border-greybd;
    min-width: 6rem;
  }
}

.return-detail {
  border-top: $border-greybd;

  .title {
    border-right: $border-greybd;
    min-width: 8rem;
  }
}

.box-bodder {
  border: $border-greybd;
  padding: 8px;
  text-align: center;
}

.classes-available-header {
  background-color: $col-primary;
}
</style>
<style lang="scss">
.p-rowgroup-footer td {
  padding: 0 !important;
}
</style>
